import React, { useState, useEffect } from 'react';
import { Layout as AntLayout, Menu, Button, Dropdown, Avatar, Space, Typography, Modal, Form, Input, message, Drawer, Upload } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ApiOutlined,
  LogoutOutlined,
  UserOutlined,
  CrownOutlined,
  KeyOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CreditCardOutlined,
  GiftOutlined,
  WechatOutlined,
  FolderOpenOutlined,
  FileTextOutlined,
  HomeOutlined,
  MenuOutlined,
  EditOutlined,
  UploadOutlined,
  BookOutlined,
  SettingOutlined,
  TrophyOutlined,
  ToolOutlined,
  AppleOutlined,
  StopOutlined,
  AppstoreOutlined,
  MobileOutlined,
  FileSearchOutlined,
  SwapOutlined,
  LayoutOutlined,
  ControlOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI, renewalAPI, adminAPI, resolveAvatarUrl } from '../services/api';
import RenewalModal from './RenewalModal';
import './Layout.css';
import './GlassCard.css';

const { Header, Sider, Content } = AntLayout;
const { Text } = Typography;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);
  const [profileVisible, setProfileVisible] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [changePasswordForm] = Form.useForm();
  const [expiryWarningVisible, setExpiryWarningVisible] = useState(false);
  const [renewalModalVisible, setRenewalModalVisible] = useState(false);
  const [renewalProductType, setRenewalProductType] = useState<'membership' | 'card' | 'order_assistant'>('membership');
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
  const [announcements, setAnnouncements] = useState<Array<{ id: number; title: string; content: string }>>([]);
  const [, setCheckingPayment] = useState(false);
  const [shouldScroll, setShouldScroll] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const announcementContentRef = React.useRef<HTMLDivElement>(null);
  const announcementContainerRef = React.useRef<HTMLDivElement>(null);
  const { user, logout, updateUser, allowedPaths, menuItems: serverMenuItems } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const iconMap: Record<string, React.ReactNode> = {
    HomeOutlined: <HomeOutlined />,
    FolderOpenOutlined: <FolderOpenOutlined />,
    FileTextOutlined: <FileTextOutlined />,
    GiftOutlined: <GiftOutlined />,
    ApiOutlined: <ApiOutlined />,
    WechatOutlined: <WechatOutlined />,
    ToolOutlined: <ToolOutlined />,
    AppleOutlined: <AppleOutlined />,
    StopOutlined: <StopOutlined />,
    FileSearchOutlined: <FileSearchOutlined />,
    SwapOutlined: <SwapOutlined />,
    KeyOutlined: <KeyOutlined />,
    SettingOutlined: <SettingOutlined />,
    MobileOutlined: <MobileOutlined />,
    CrownOutlined: <CrownOutlined />,
    LayoutOutlined: <LayoutOutlined />,
    ControlOutlined: <ControlOutlined />,
  };

  const buildMenuItems = (items: any[]): any[] => {
    return items.map(item => {
      const menuItem: any = {
        key: item.key,
        icon: iconMap[item.icon] || <AppstoreOutlined />,
        label: item.label,
      };
      if (item.children && item.children.length > 0) {
        menuItem.children = buildMenuItems(item.children);
      }
      return menuItem;
    });
  };

  const menuItems = buildMenuItems(serverMenuItems);

  useEffect(() => {
    const checkMobile = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 480);
      if (width <= 768) {
        setCollapsed(true);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const getSelectedKeys = () => {
    const path = location.pathname;
    const dataMenuPaths = ['/data', '/invites', '/api-docs', '/wechat-auth'];
    const cardMenuPaths = ['/card-management', '/card-management/dictionary', '/card-management/record-logs', '/card-management/system-config', '/card-management/interface-manage', '/card-management/interface-list'];
    const toolsMenuPaths = ['/tools/ios-op-login', '/tools/op-game-block', '/tools/text-processor', '/tools/api-charge-list', '/tools/converter'];
    if (dataMenuPaths.includes(path)) {
      return [path, 'data'];
    }
    if (cardMenuPaths.includes(path)) {
      return [path, 'card-management'];
    }
    if (toolsMenuPaths.includes(path)) {
      return [path, 'tools'];
    }
    if (path === '/' || path === '/home') {
      return ['/home'];
    }
    return [path];
  };

  const handleMenuClick = ({ key }: { key: string }) => {
    setDrawerVisible(false);
    navigate(key);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleChangePassword = () => {
    setChangePasswordVisible(true);
  };

  const handleChangePasswordSubmit = async (values: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => {
    try {
      const response = await authAPI.changePassword(values);
      if (response.data.status === 200) {
        message.success('密码修改成功，请重新登录');
        setChangePasswordVisible(false);
        changePasswordForm.resetFields();
        setTimeout(() => {
          logout();
          navigate('/login');
        }, 1500);
      } else {
        message.error(response.data.error || '密码修改失败');
      }
    } catch (error: any) {
      message.error(error.response?.data?.error || '密码修改失败');
    }
  };

  const handleEditProfile = () => {
    setAvatarPreview(resolveAvatarUrl(user?.avatar_url) || '/ico.jpg');
    setAvatarFile(null);
    setProfileVisible(true);
  };

  const handleAvatarChange = (info: any) => {
    const file = info.file?.originFileObj || info.file;
    if (file) {
      const isImage = file.type?.startsWith('image/');
      if (!isImage) {
        message.error('只能上传图片文件');
        return;
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error('图片大小不能超过2MB');
        return;
      }
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatarPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleProfileSubmit = async () => {
    if (!avatarFile) {
      message.info('请先选择新头像');
      return;
    }
    try {
      setUploadingAvatar(true);
      const formData = new FormData();
      formData.append('avatar', avatarFile);
      const response = await authAPI.updateProfile(formData);
      if (response.data.status === 200) {
        message.success('头像修改成功');
        const newAvatarUrl = response.data.avatar_url;
        await updateUser({
          ...user!,
          avatar_url: newAvatarUrl,
        });
        setProfileVisible(false);
        setAvatarFile(null);
      } else {
        message.error(response.data.error || '头像修改失败');
      }
    } catch (error: any) {
      message.error(error.response?.data?.error || '头像修改失败');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const formatExpiryTime = (date: Date) => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();
    if (hours === 0 && minutes === 0 && seconds === 0) {
      return date.toLocaleDateString();
    }
    return date.toLocaleString();
  };

  const checkExpiryWarning = () => {
    if (!user?.expires_at) return null;
    if (user?.role === 'super_admin' || user?.role === 'permanent') return null;
    const nowUTC = Date.now();
    const localOffset = new Date().getTimezoneOffset();
    const beijingOffset = -8 * 60;
    const beijingNow = new Date(nowUTC + (localOffset - beijingOffset) * 60 * 1000);
    const expiresStr = String(user.expires_at);
    const beijingTimeStr = expiresStr.replace(' ', 'T') + '+08:00';
    const expires = new Date(beijingTimeStr);
    const isExpired = beijingNow.getTime() > expires.getTime();
    const daysUntilExpiry = Math.ceil((expires.getTime() - beijingNow.getTime()) / (1000 * 60 * 60 * 24));
    return {
      isExpired,
      daysUntilExpiry,
      expires,
      isExpiringSoon: !isExpired && daysUntilExpiry <= 3 && daysUntilExpiry > 0
    };
  };

  const checkAccountDisabled = () => {
    if (!user) return false;
    if (!user.expires_at) return false;
    if (user.is_active === undefined || user.is_active === null) return false;
    if (user.is_active === true) return false;
    if (user.is_active !== false) return false;
    const expiryInfo = checkExpiryWarning();
    return expiryInfo && !expiryInfo.isExpired;
  };

  useEffect(() => {
    const checkPayment = async () => {
      if (!user) return;
      try {
        setCheckingPayment(true);
        const response = await renewalAPI.checkPaymentAvailable();
        if (response.data.status === 200) {
          setHasPaymentMethod(response.data.data?.available || false);
        } else {
          setHasPaymentMethod(false);
        }
      } catch (error) {
        console.error('检查支付方式失败:', error);
        setHasPaymentMethod(false);
      } finally {
        setCheckingPayment(false);
      }
    };
    checkPayment();
  }, [user]);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const response = await adminAPI.getActiveAnnouncements();
        if (response.data.status === 200) {
          setAnnouncements(response.data.data || []);
        }
      } catch (error) {
        console.error('获取公告失败:', error);
      }
    };
    if (user) {
      fetchAnnouncements();
      const interval = setInterval(fetchAnnouncements, 10000);
      return () => clearInterval(interval);
    }
  }, [user]);

  useEffect(() => {
    const checkScrollNeeded = () => {
      if (announcementContentRef.current && announcementContainerRef.current) {
        const contentWidth = announcementContentRef.current.scrollWidth;
        const containerWidth = announcementContainerRef.current.clientWidth;
        setShouldScroll(contentWidth > containerWidth);
      }
    };
    const timer = setTimeout(checkScrollNeeded, 100);
    window.addEventListener('resize', checkScrollNeeded);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkScrollNeeded);
    };
  }, [announcements]);

  useEffect(() => {
    const handleAccountDisabledExpired = async () => {
      try {
        const response = await authAPI.getProfile();
        if (response.data.user) {
          const updatedUser = response.data.user;
          await updateUser(updatedUser);
        }
      } catch (error) {
        message.error('账号已被禁用，请联系管理员');
        setTimeout(() => {
          logout();
          navigate('/login');
        }, 2000);
      }
    };

    const handleShowRenewalModal = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail && (detail.productType === 'card' || detail.productType === 'order_assistant')) {
        setRenewalProductType(detail.productType);
      } else {
        setRenewalProductType('membership');
      }
      setRenewalModalVisible(true);
    };

    window.addEventListener('account-disabled-expired', handleAccountDisabledExpired);
    window.addEventListener('show-renewal-modal', handleShowRenewalModal);
    return () => {
      window.removeEventListener('account-disabled-expired', handleAccountDisabledExpired);
      window.removeEventListener('show-renewal-modal', handleShowRenewalModal);
    };
  }, [logout, navigate, updateUser]);

  useEffect(() => {
    if (!user) return;
    const isDisabled = checkAccountDisabled();
    if (isDisabled) {
      message.error('账号已被禁用，请联系管理员');
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 2000);
      return;
    }

    if ((user?.role || 'normal') === 'normal') return;

    const expiryInfo = checkExpiryWarning();
    if (expiryInfo?.isExpired) {
      return;
    }

    if (expiryInfo?.isExpiringSoon) {
      const today = new Date().toDateString();
      const lastWarningDate = localStorage.getItem('lastExpiryWarning');
      if (lastWarningDate !== today) {
        renewalAPI.checkPaymentAvailable()
          .then((response) => {
            if (response.data.status === 200) {
              setHasPaymentMethod(response.data.data?.available || false);
            } else {
              setHasPaymentMethod(false);
            }
            setExpiryWarningVisible(true);
            localStorage.setItem('lastExpiryWarning', today);
          })
          .catch((error) => {
            setHasPaymentMethod(false);
            setExpiryWarningVisible(true);
            localStorage.setItem('lastExpiryWarning', today);
          });
      }
    }
  }, [user]);

  const userMenuItems = [
    {
      key: 'editProfile',
      icon: <EditOutlined />,
      label: '修改资料',
      onClick: handleEditProfile,
    },
    {
      key: 'changePassword',
      icon: <KeyOutlined />,
      label: '修改密码',
      onClick: handleChangePassword,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: (e: any) => {
        if (e?.domEvent?.target) {
          (e.domEvent.target as HTMLElement).setAttribute('data-logout-action', 'true');
        }
        handleLogout();
      },
    },
  ];

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      {isMobile ? (
        <Drawer
          placement="left"
          closable={false}
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          width={250}
          className="sider-drawer"
          styles={{ body: { padding: 0 } }}
        >
          <div className="sider-logo">
            <span className="sider-logo-text">Tomato 综合站</span>
          </div>
          <Menu
            mode="inline"
            defaultOpenKeys={['data', 'card-management', 'tools']}
            selectedKeys={getSelectedKeys()}
            items={menuItems}
            onClick={handleMenuClick}
            style={{ borderRight: 0 }}
          />
        </Drawer>
      ) : (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          className="layout-sider"
          style={{
            background: 'rgba(255, 255, 255, 0.75)',
            backdropFilter: 'blur(16px) saturate(160%)',
            WebkitBackdropFilter: 'blur(16px) saturate(160%)',
            boxShadow: '2px 0 8px rgba(0,0,0,0.06)',
            borderRight: '1px solid rgba(0, 0, 0, 0.06)',
          }}
        >
          <div className="sider-logo" style={{ fontSize: collapsed ? '16px' : '18px' }}>
            <span className="sider-logo-text">{collapsed ? 'Tomato' : 'Tomato 综合站'}</span>
          </div>
          <Menu
            mode="inline"
            defaultOpenKeys={['data', 'card-management', 'tools']}
            selectedKeys={getSelectedKeys()}
            items={menuItems}
            onClick={handleMenuClick}
            style={{ borderRight: 0 }}
          />
        </Sider>
      )}
      <AntLayout>
        <Header className="layout-header" style={{
          padding: '0 16px',
          background: 'rgba(255, 255, 255, 0.75)',
          backdropFilter: 'blur(16px) saturate(160%)',
          WebkitBackdropFilter: 'blur(16px) saturate(160%)',
          display: 'flex',
          alignItems: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
        }}>
          <div style={{ flexShrink: 0 }}>
            {isMobile ? (
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setDrawerVisible(true)}
                style={{
                  fontSize: '18px',
                  width: 48,
                  height: 48,
                }}
              />
            ) : (
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                style={{
                  fontSize: '16px',
                  width: 64,
                  height: 64,
                }}
              />
            )}
          </div>
          {!isMobile && announcements && announcements.length > 0 && (
            <div
              ref={announcementContainerRef}
              className="announcement-container"
              style={{
                flex: 1,
                margin: '0 24px',
                height: '40px',
                overflow: 'hidden',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '4px',
                padding: '0 12px',
              }}
            >
              <div
                ref={announcementContentRef}
                className="announcement-scroll"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '32px',
                  whiteSpace: 'nowrap',
                  position: shouldScroll ? 'absolute' : 'relative',
                  left: shouldScroll ? 12 : 'auto',
                  top: shouldScroll ? '50%' : 'auto',
                  transform: shouldScroll ? 'translateY(-50%)' : 'none',
                  animation: shouldScroll ? 'scroll-announcement 30s linear infinite' : 'none',
                }}
              >
                {announcements.map((announcement, index) => (
                  <React.Fragment key={announcement.id}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', color: '#000', fontSize: '14px', lineHeight: '1.5' }}>
                      <Text style={{ color: '#000' }}>{announcement.content}</Text>
                    </div>
                    {index < announcements.length - 1 && (
                      <span style={{ color: '#d9d9d9', fontSize: '16px' }}>|</span>
                    )}
                  </React.Fragment>
                ))}
                {shouldScroll && announcements.map((announcement, index) => (
                  <React.Fragment key={`${announcement.id}-dup`}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', color: '#000', fontSize: '14px', lineHeight: '1.5' }}>
                      <Text style={{ color: '#000' }}>{announcement.content}</Text>
                    </div>
                    {index < announcements.length - 1 && (
                      <span style={{ color: '#d9d9d9', fontSize: '16px' }}>|</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
              <style>
                {`
                  @keyframes scroll-announcement {
                    0% { transform: translateY(-50%) translateX(0); }
                    100% { transform: translateY(-50%) translateX(-50%); }
                  }
                `}
              </style>
            </div>
          )}
          <div style={{ flexShrink: 0, marginLeft: 'auto' }}>
            <Space size={isMobile ? 'small' : 'large'} className={isMobile ? 'header-right-mobile' : ''}>
              <Space size="small">
                {!isMobile && <Text strong>欢迎，{user?.username}</Text>}
                <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" arrow>
                  <Avatar
                    style={{ backgroundColor: '#1890ff', cursor: 'pointer' }}
                    src={resolveAvatarUrl(user?.avatar_url)}
                    icon={!user?.avatar_url ? <UserOutlined /> : undefined}
                  />
                </Dropdown>
              </Space>
            </Space>
          </div>
        </Header>
        <Content className="layout-content" style={{
          margin: isMobile ? '12px' : '24px',
          padding: isMobile ? '12px' : '24px',
          background: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(16px) saturate(160%)',
          WebkitBackdropFilter: 'blur(16px) saturate(160%)',
          borderRadius: '1.8rem',
          border: '1px solid rgba(0, 0, 0, 0.06)',
          boxShadow: '0 15px 35px -12px rgba(0, 0, 0, 0.08)',
          minHeight: 'calc(100vh - 112px)',
        }}>
          {children}
        </Content>
      </AntLayout>

      <Modal
        title="修改密码"
        open={changePasswordVisible}
        onCancel={() => {
          setChangePasswordVisible(false);
          changePasswordForm.resetFields();
        }}
        onOk={() => changePasswordForm.submit()}
        okText="修改"
        cancelText="取消"
        destroyOnHidden
      >
        <Form form={changePasswordForm} layout="vertical" onFinish={handleChangePasswordSubmit}>
          <Form.Item name="currentPassword" label="当前密码" rules={[{ required: true, message: '请输入当前密码' }]}>
            <Input.Password placeholder="请输入当前密码" />
          </Form.Item>
          <Form.Item name="newPassword" label="新密码" rules={[{ required: true, message: '请输入新密码' }, { min: 6, message: '密码长度至少6位' }]}>
            <Input.Password placeholder="请输入新密码" />
          </Form.Item>
          <Form.Item name="confirmPassword" label="确认新密码" dependencies={['newPassword']} rules={[{ required: true, message: '请确认新密码' }, ({ getFieldValue }) => ({ validator(_, value) { if (!value || getFieldValue('newPassword') === value) { return Promise.resolve(); } return Promise.reject(new Error('两次输入的密码不一致')); } })]}>
            <Input.Password placeholder="请再次输入新密码" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="修改资料"
        open={profileVisible}
        onCancel={() => {
          setProfileVisible(false);
          setAvatarFile(null);
        }}
        onOk={handleProfileSubmit}
        okText="保存"
        cancelText="取消"
        confirmLoading={uploadingAvatar}
        destroyOnHidden
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
          <Avatar
            size={96}
            src={avatarPreview || undefined}
            icon={!avatarPreview ? <UserOutlined /> : undefined}
            style={{ backgroundColor: '#1890ff' }}
          />
          <Upload
            accept="image/*"
            showUploadList={false}
            beforeUpload={() => false}
            onChange={handleAvatarChange}
          >
            <Button icon={<UploadOutlined />}>选择头像</Button>
          </Upload>
          <div style={{ color: '#999', fontSize: '12px' }}>
            支持 JPG、PNG、GIF、WebP 格式，大小不超过 2MB
          </div>
        </div>
      </Modal>

      <Modal
        title={<Space><ExclamationCircleOutlined style={{ color: '#faad14' }} /><span>账户到期提醒</span></Space>}
        open={expiryWarningVisible}
        onOk={() => setExpiryWarningVisible(false)}
        onCancel={() => setExpiryWarningVisible(false)}
        okText="我知道了"
        cancelText="关闭"
        centered
        footer={[
          <Button key="close" onClick={() => setExpiryWarningVisible(false)}>关闭</Button>,
          hasPaymentMethod && (
            <Button key="renew" type="primary" icon={<CreditCardOutlined />} onClick={async () => {
              try {
                const response = await renewalAPI.checkPaymentAvailable();
                if (response.data.status === 200 && response.data.data?.available) {
                  setExpiryWarningVisible(false);
                  setRenewalModalVisible(true);
                } else {
                  message.warning('续费功能暂未启用，请联系管理员');
                  setExpiryWarningVisible(false);
                }
              } catch (error) {
                message.warning('续费功能暂未启用，请联系管理员');
                setExpiryWarningVisible(false);
              }
            }}>去续费</Button>
          ),
          <Button key="know" type="primary" onClick={() => setExpiryWarningVisible(false)}>我知道了</Button>,
        ].filter(Boolean)}
      >
        {(() => {
          const expiryInfo = checkExpiryWarning();
          if (!expiryInfo?.isExpiringSoon) return null;
          return (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <ExclamationCircleOutlined style={{ fontSize: '48px', color: '#faad14', marginBottom: '16px' }} />
              <div style={{ fontSize: '16px', marginBottom: '12px' }}><Text strong>您的账户快到期了，请注意！</Text></div>
              <div style={{ fontSize: '14px', color: '#666' }}>您的账户将在 <Text strong type="warning">{expiryInfo.daysUntilExpiry} 天</Text> 后到期</div>
              <div style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>到期时间：{formatExpiryTime(expiryInfo.expires)}</div>
              <div style={{ fontSize: '14px', color: '#999', marginTop: '12px' }}>请及时续期，以免影响正常使用</div>
            </div>
          );
        })()}
      </Modal>

      <RenewalModal
        visible={renewalModalVisible}
        isExpired={checkExpiryWarning()?.isExpired || false}
        productType={renewalProductType}
        onCancel={() => setRenewalModalVisible(false)}
        onSuccess={() => {
          setRenewalModalVisible(false);
          window.location.reload();
        }}
      />
    </AntLayout>
  );
};

export default Layout;
